import {useCallback, useMemo, useState} from 'react';
import {PaymentMethodsSelect} from '../PaymentMethodsSelect';
import {decimalFormat, translate} from '../../utils';
import DecimalInput from '../DecimalInput';

export function InvoiceDraftPaymentMethods({paymentMethods, paymentMethodAmounts, setPaymentMethodAmounts, totalAmount}) {
    if (paymentMethodAmounts.every(paymentMethodAmount => paymentMethodAmount.amount === null)) {
        return <InvoiceDraftPaymentMethodsSingle paymentMethods={paymentMethods} paymentMethodAmounts={paymentMethodAmounts} setPaymentMethodAmounts={setPaymentMethodAmounts}/>
    } else {
        return <InvoiceDraftPaymentMethodsSplit paymentMethods={paymentMethods} paymentMethodAmounts={paymentMethodAmounts} setPaymentMethodAmounts={setPaymentMethodAmounts} totalAmount={totalAmount}/>
    }
}

function InvoiceDraftPaymentMethodsSingle({paymentMethods, paymentMethodAmounts, setPaymentMethodAmounts}) {
    const paymentMethodOptions = useMemo(() => paymentMethodSelectOptions(paymentMethods), [paymentMethods]);
    const selectedOption = paymentMethodOptions.find(option => option.value === paymentMethodAmounts[0].paymentMethod);

    const onChange = useCallback(selectedOption => {
        setPaymentMethodAmounts([
            {paymentMethod: selectedOption.value, amount: null},
            {paymentMethod: null, amount: null},
            {paymentMethod: null, amount: null},
        ]);
    }, [setPaymentMethodAmounts]);

    return <div className={`form-group mb-0 ${paymentMethodAmounts[0].error ? 'has-error' : ''}`}>
        <PaymentMethodsSelect className={'react-select-container'} classNamePrefix={'react-select'} options={paymentMethodOptions} placeholder={translate('frontend', 'Select Payment Method')} value={selectedOption} onChange={onChange}/>
        {paymentMethodAmounts[0].error ? <p className="help-block">{paymentMethodAmounts[0].error}</p> : null}
    </div>;
}

function InvoiceDraftPaymentMethodsSplit({paymentMethods, paymentMethodAmounts, setPaymentMethodAmounts, totalAmount}) {
    // `setPaymentMethodAmounts` expects valid values, but, while amount is being changed,
    // the values might temporarily be invalid. For parent state to have only valid state,
    // we delay setting it until user finishes their input (blur event)
    const [hasAnyChange, setAnyChange] = useState(false);
    const [inputs, setInputs] = useState(() => {
        return paymentMethodAmounts.map(paymentMethodAmount => ({
            paymentMethod: paymentMethodAmount.paymentMethod,
            amount: paymentMethodAmount.amount,
            amountInput: decimalFormat(paymentMethodAmount.amount, 2),
        }));
    });

    // Parent state might have changed independently. If so, re-copy the state.
    if (!hasAnyChange && paymentMethodAmounts.some((paymentMethodAmount, index) => paymentMethodAmount.paymentMethod !== inputs[index].paymentMethod || paymentMethodAmount.amount !== inputs[index].amount)) {
        setInputs(paymentMethodAmounts.map(paymentMethodAmount => ({
            paymentMethod: paymentMethodAmount.paymentMethod,
            amount: paymentMethodAmount.amount,
            amountInput: decimalFormat(paymentMethodAmount.amount, 2),
        })));
    }

    function onChangePaymentMethod(index, selectedOption) {
        // Can change the state immediately without waiting for a blur event
        const nextPaymentMethodAmounts = [...paymentMethodAmounts];
        nextPaymentMethodAmounts[index] = {
            paymentMethod: selectedOption.value,
            amount: paymentMethodAmounts[index].amount,
        };
        setPaymentMethodAmounts(nextPaymentMethodAmounts);
    }

    function onChangeAmount(index, event) {
        const nextInputs = [...inputs];
        nextInputs[index] = {
            paymentMethod: nextInputs[index].paymentMethod,
            amount: numberFormat(event.target.value, 2, 2, window.config.basicSettings.default_numeral_format) || 0.0,
            amountInput: event.target.value,
        };

        const remaining = nextInputs.reduce((carry, paymentMethodAmount) => carry - paymentMethodAmount.amount, totalAmount);
        if (index + 1 < nextInputs.length) {
            const amount = parseFloat((nextInputs[index + 1].amount + remaining).toFixed(2));
            nextInputs[index + 1] = {
                paymentMethod: nextInputs[index + 1].paymentMethod,
                amount: amount,
                amountInput: decimalFormat(amount, 2),
            };
        }

        setInputs(nextInputs);
        setAnyChange(true);
    }

    function onBlur() {
        if (hasAnyChange) {
            setPaymentMethodAmounts(inputs.map(input => ({
                paymentMethod: input.paymentMethod,
                amount: input.amount,
            })));
        }
        setAnyChange(false);
    }

    function onFocus() {
        setAnyChange(false);
    }

    const paymentMethodOptions = useMemo(() => paymentMethodSelectOptions(paymentMethods), [paymentMethods]);

    return inputs.map((input, i) => (
        <div key={`payment_method_${i}`} className={`form-group mb-0 row ${paymentMethodAmounts[i].error ? 'has-error' : ''}`} onBlur={onBlur} onFocus={onFocus}>
            <div className="col-md-8">
                <PaymentMethodsSelect className={'react-select-container'} classNamePrefix={'react-select'} options={paymentMethodOptions} placeholder={translate('frontend', 'Select Payment Method')} value={paymentMethodOptions.find(option => option.value === input.paymentMethod)} onChange={event => onChangePaymentMethod(i, event)}/>
                {paymentMethodAmounts[i].error ? <p className="help-block">{paymentMethodAmounts[i].error}</p> : null}
            </div>
            <div className="col-md-4">
                <div className="form-group">
                    <DecimalInput className={'form-control'} value={input.amountInput} onChange={event => onChangeAmount(i, event)}/>
                </div>
            </div>
        </div>
    ));
}

function paymentMethodSelectOptions(paymentMethods) {
    return paymentMethods.map((paymentMethod) => {
        return {
            value: paymentMethod.id,
            label: paymentMethod.name,
            icon: paymentMethod.icon,
        };
    });
}
